import { IconLock, IconPencil } from "@tabler/icons-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { BREAK_STYLE, CELL_STYLE } from "../lib/cellStyles"
import { CELL_STATE_LABEL, type CellState } from "../lib/editorModel"
import { OVERRIDE_KIND_LABEL, type OverrideKindKey } from "../lib/overrideOverlay"
import { OverrideKindIcon } from "./OverrideKindIcon"

/** What a cell an override changed carries (AB#803): drawn only on the dated week. */
export interface EditorCellMarker {
  kinds: readonly OverrideKindKey[]
  /** Kind, Pacific window and target of each override, one per line: the tooltip. */
  description: string
  /** Open the override's detail. */
  onOpen: () => void
}

/**
 * One cell of the availability grid (AB#781). Not the calendar's SlotCell, which draws a booking:
 * this draws a state, and in edit mode a click moves it to the next one. A break is locked — drawn,
 * named and never clickable.
 *
 * With a `marker` (AB#803) it is a cell an override changed: still drawn in its effective state, plus
 * a dashed outline and one icon per override kind (the non-colour cue), a tooltip naming the
 * overrides on hover or focus, and a click that opens the detail. Without one it is drawn exactly as
 * it always was.
 *
 * With `pending` (AB#806) it lies inside the window of an override being made: a solid inset ring and
 * a pencil (the non-colour cue) over its current state, named in its accessible name. Without it,
 * unchanged.
 */
export function EditorCell({
  state,
  locked,
  editable,
  label,
  onCycle,
  marker,
  pending,
}: {
  state: CellState
  locked: boolean
  editable: boolean
  /** "Monday 9:00 AM", for the accessible name. */
  label: string
  onCycle: () => void
  marker?: EditorCellMarker
  /** The kind of the override being made, when this cell lies inside its window. */
  pending?: string
}) {
  const name = locked ? "Break" : CELL_STATE_LABEL[state]

  if (marker) return <MarkedCell state={state} locked={locked} name={name} label={label} marker={marker} pending={pending} />

  const clickable = editable && !locked

  return (
    <button
      type="button"
      className={cn(
        "flex h-7 w-full items-center justify-center gap-1 rounded-sm px-1 text-[11px] font-medium leading-none",
        clickable ? "cursor-pointer hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" : "cursor-default",
        pending !== undefined && PENDING_CLASS,
      )}
      style={locked ? BREAK_STYLE : CELL_STYLE[state]}
      disabled={!clickable}
      aria-label={`${label}: ${name}${locked ? " (locked)" : ""}${pendingSuffix(pending)}`}
      data-slot="availability-cell"
      data-state={locked ? "break" : state}
      data-override-pending={pending !== undefined ? "" : undefined}
      onClick={clickable ? onCycle : undefined}
    >
      {locked && <IconLock className="size-3" aria-hidden />}
      {state === "unavailable" && !locked ? "" : name}
      {pending !== undefined && <IconPencil className="size-3 shrink-0" aria-hidden data-pending-icon="" />}
    </button>
  )
}

/** The pending override's ring (AB#806): solid and inset, where a saved override's marker is dashed. */
const PENDING_CLASS = "ring-2 ring-inset ring-primary"

function pendingSuffix(pending: string | undefined): string {
  return pending === undefined ? "" : `, inside the pending override: ${pending}`
}

function MarkedCell({
  state,
  locked,
  name,
  label,
  marker,
  pending,
}: {
  state: CellState
  locked: boolean
  name: string
  label: string
  marker: EditorCellMarker
  pending?: string
}) {
  const kinds = marker.kinds.map(kind => OVERRIDE_KIND_LABEL[kind]).join(", ")

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-7 w-full cursor-pointer items-center justify-center gap-1 rounded-sm px-1 text-[11px] font-medium leading-none",
            "outline-dashed outline-1 -outline-offset-2 outline-foreground/60 hover:brightness-95",
            "focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-ring",
            pending !== undefined && PENDING_CLASS,
          )}
          style={locked ? BREAK_STYLE : CELL_STYLE[state]}
          aria-label={`${label}: ${name}${locked ? " (locked)" : ""}, changed by an override: ${kinds}${pendingSuffix(pending)}`}
          aria-haspopup="dialog"
          data-slot="availability-cell"
          data-state={locked ? "break" : state}
          data-override-marked=""
          data-override-pending={pending !== undefined ? "" : undefined}
          onClick={marker.onOpen}
        >
          {locked && <IconLock className="size-3" aria-hidden />}
          {state === "unavailable" && !locked ? "" : <span className="truncate">{name}</span>}
          {marker.kinds.map(kind => <OverrideKindIcon key={kind} kind={kind} />)}
          {pending !== undefined && <IconPencil className="size-3 shrink-0" aria-hidden data-pending-icon="" />}
        </button>
      </TooltipTrigger>
      <TooltipContent className="whitespace-pre-line text-left" data-testid="override-tooltip">
        {marker.description}
      </TooltipContent>
    </Tooltip>
  )
}
